import { Droplets, Brush, Shield, Crown } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface ServiceTier {
  id: string;
  name: string;
  icon: LucideIcon;
  price: number;
  displayPrice: string;
  regularPrice: string | null;
  period: string;
  tier: number;
  popular: boolean;
  gradient: string;
  shortDescription: string;
  longDescription: string;
  featuresAbbreviated: string[];
  featuresStandard: string[];
  featuresFull: ServiceFeatureCategories;
}

export interface ServiceFeatureCategories {
  weeklyService: ServiceFeature[];
  reporting: ServiceFeature[];
  specialtyChemicals: ServiceFeature[];
  periodicMaintenance: ServiceFeature[];
  repairsWarranty: ServiceFeature[];
  prioritySupport: ServiceFeature[];
  luxuryAmenities?: ServiceFeature[];
}

export interface ServiceFeature {
  name: string;
  value?: string;
  basic: boolean;
  full: boolean;
  premium: boolean;
  lux: boolean;
}

export const serviceTiersData: Record<string, ServiceTier> = {
  basic: {
    id: "basic",
    name: "Basic",
    icon: Droplets,
    price: 85,
    displayPrice: "$85",
    regularPrice: null,
    period: "month",
    tier: 0,
    popular: false,
    gradient: "from-blue-light/20 to-accent/20",
    shortDescription: "Bi-weekly chemistry care for pools with minimal maintenance needs",
    longDescription: "Basic is a chemistry-focused plan, not a full cleaning plan. Perfect for pool owners who handle their own cleaning but want professional water chemistry management.",
    featuresAbbreviated: [
      "Bi-weekly service visits",
      "Water testing & balancing",
      "Basic chemicals included",
      "Skimmer/pump basket emptying"
    ],
    featuresStandard: [
      "Bi-weekly water testing & balancing",
      "Emptying skimmer & pump baskets (as needed)",
      "Basic equipment inspection (visual/operational)",
      "Basic chemicals included (chlorine, acid/base, shock)",
      "Digital service report",
      "⚠️ Does NOT include brushing, vacuuming, or tile cleaning",
      "⚠️ Filter cleans billed separately ($150 each)",
      "⚠️ Repairs billed separately"
    ],
    featuresFull: {
      weeklyService: [
        { name: "Service Visit Frequency", value: "Bi-weekly (Basic) / Weekly (Full+)", basic: true, full: true, premium: true, lux: true },
        { name: "Equipment Visual Inspection", basic: true, full: true, premium: true, lux: true },
        { name: "Chemical Testing & Water Balancing", basic: true, full: true, premium: true, lux: true },
        { name: "Emptying Skimmer & Pump Baskets", basic: true, full: true, premium: true, lux: true },
        { name: "Skimming Surface Debris", basic: false, full: true, premium: true, lux: true },
        { name: "Brushing Walls, Steps, and Tile Line", basic: false, full: true, premium: true, lux: true },
        { name: "Manual Vacuuming as Needed", basic: false, full: true, premium: true, lux: true },
        { name: "Backwashing as Needed", basic: false, full: true, premium: true, lux: true }
      ],
      reporting: [
        { name: "Digital Service Log w/ Time Stamp", basic: true, full: true, premium: true, lux: true },
        { name: "Text/Email Photo Report After Visit", basic: true, full: true, premium: true, lux: true },
        { name: "Realtime Smart Water Monitoring Integration (coming soon)", basic: false, full: false, premium: false, lux: true }
      ],
      specialtyChemicals: [
        { name: "Phosphate Remover (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Enzyme Treatments (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Stabilizer (CYA) Monitoring & Dosing (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Algaecide Treatments (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Scale & Calcium Prevention (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Aromatherapy/Spa Chemical Upgrades", basic: false, full: false, premium: false, lux: true }
      ],
      periodicMaintenance: [
        { name: "Filter Deep Cleaning (2×/year)", value: "$300 value ($150 x 2 times/year)", basic: false, full: false, premium: true, lux: true },
        { name: "Salt Cell Cleaning (4×/year)", value: "$200 value ($50 x 4 times/year)", basic: false, full: false, premium: true, lux: true }
      ],
      repairsWarranty: [
        { name: "On-Route Minor Repairs (up to 30 min labor)", value: "Gaskets, o-rings, minor part replacements", basic: false, full: false, premium: true, lux: true },
        { name: "ALL Repair Labor Covered (no caps)", value: "Unlimited labor coverage, parts at retail", basic: false, full: false, premium: false, lux: true },
        { name: "90-Day Labor Warranty on Repairs", basic: false, full: false, premium: true, lux: true },
        { name: "ALL Equipment Fully Warrantied", value: "Pumps, heaters, automation - everything", basic: false, full: false, premium: false, lux: true }
      ],
      prioritySupport: [
        { name: "Priority Scheduling (Route Priority)", basic: false, full: false, premium: true, lux: true },
        { name: "Priority Repair Scheduling (Front of Queue)", basic: false, full: false, premium: true, lux: true },
        { name: "Same-Day Emergency Response", value: "$299 dispatch fee", basic: false, full: false, premium: true, lux: true },
        { name: "Dedicated Concierge Contact (Direct Cell Access)", basic: false, full: false, premium: false, lux: true }
      ]
    }
  },
  full: {
    id: "full",
    name: "Full",
    icon: Brush,
    price: 180,
    displayPrice: "$180",
    regularPrice: null,
    period: "month",
    tier: 1,
    popular: false,
    gradient: "from-primary/20 to-blue-light/20",
    shortDescription: "Weekly standard service for reliable, professional pool care",
    longDescription: "Full is BlueLux's core weekly maintenance plan for reliable, professional pool care. Includes complete cleaning, brushing, vacuuming, and chemical management.",
    featuresAbbreviated: [
      "Weekly service visits",
      "Complete cleaning & debris removal",
      "Brushing, vacuuming as needed",
      "Basic chemicals included"
    ],
    featuresStandard: [
      "Weekly surface skimming & debris removal",
      "Brushing walls/steps/fixtures (as needed)",
      "Vacuuming as needed (based on debris/clarity)",
      "Emptying skimmer & pump baskets",
      "Basic equipment inspection & issue reporting",
      "Basic chemicals included (chlorine, acid/base, shock)",
      "Digital service report with photos",
      "⚠️ Filter clean: $150 each (recommended 2×/year)",
      "⚠️ Salt cell clean: $50 each",
      "⚠️ Specialty chemicals & repairs billed separately"
    ],
    featuresFull: {
      weeklyService: [
        { name: "Equipment Visual Inspection", basic: true, full: true, premium: true, lux: true },
        { name: "Chemical Testing & Water Balancing", basic: true, full: true, premium: true, lux: true },
        { name: "Emptying Skimmer & Pump Baskets", basic: true, full: true, premium: true, lux: true },
        { name: "Skimming Surface Debris", basic: false, full: true, premium: true, lux: true },
        { name: "Brushing Walls, Steps, and Tile Line", basic: false, full: true, premium: true, lux: true },
        { name: "Manual Vacuuming as Needed", basic: false, full: true, premium: true, lux: true },
        { name: "Backwashing as Needed", basic: false, full: true, premium: true, lux: true }
      ],
      reporting: [
        { name: "Digital Service Log w/ Time Stamp", basic: true, full: true, premium: true, lux: true },
        { name: "Text/Email Photo Report After Visit", basic: true, full: true, premium: true, lux: true },
        { name: "Realtime Smart Water Monitoring Integration (coming soon)", basic: false, full: false, premium: false, lux: true }
      ],
      specialtyChemicals: [
        { name: "Phosphate Remover (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Enzyme Treatments (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Stabilizer (CYA) Monitoring & Dosing (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Algaecide Treatments (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Scale & Calcium Prevention (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Aromatherapy/Spa Chemical Upgrades", basic: false, full: false, premium: false, lux: true }
      ],
      periodicMaintenance: [
        { name: "Filter Deep Cleaning (2×/year)", value: "$300 value ($150 x 2 times/year)", basic: false, full: false, premium: true, lux: true },
        { name: "Salt Cell Cleaning (4×/year)", value: "$200 value ($50 x 4 times/year)", basic: false, full: false, premium: true, lux: true }
      ],
      repairsWarranty: [
        { name: "On-Route Minor Repairs (up to 30 min labor)", value: "Gaskets, o-rings, minor part replacements", basic: false, full: false, premium: true, lux: true },
        { name: "ALL Repair Labor Covered (no caps)", value: "Unlimited labor coverage, parts at retail", basic: false, full: false, premium: false, lux: true },
        { name: "90-Day Labor Warranty on Repairs", basic: false, full: false, premium: true, lux: true },
        { name: "ALL Equipment Fully Warrantied", value: "Pumps, heaters, automation - everything", basic: false, full: false, premium: false, lux: true }
      ],
      prioritySupport: [
        { name: "Priority Scheduling (Route Priority)", basic: false, full: false, premium: true, lux: true },
        { name: "Priority Repair Scheduling (Front of Queue)", basic: false, full: false, premium: true, lux: true },
        { name: "Same-Day Emergency Response", value: "$299 dispatch fee", basic: false, full: false, premium: true, lux: true },
        { name: "Dedicated Concierge Contact (Direct Cell Access)", basic: false, full: false, premium: false, lux: true }
      ]
    }
  },
  premium: {
    id: "premium",
    name: "Premium",
    icon: Shield,
    price: 350,
    displayPrice: "$350",
    regularPrice: "$500+",
    period: "month",
    tier: 2,
    popular: true,
    gradient: "from-gold/20 to-primary/20",
    shortDescription: "Predictable & proactive care - reduce surprises",
    longDescription: "Premium is designed to reduce surprises, prevent problems, and provide predictable monthly costs rather than unpredictable add-ons. Includes filter cleans, salt cell cleans, all specialty chemicals, and on-route minor repairs.",
    featuresAbbreviated: [
      "Everything in Full",
      "2 filter cleans/year included",
      "4 salt cell cleans/year included",
      "30-min on-route labor included"
    ],
    featuresStandard: [
      "Everything in Full package +",
      "Two filter deep cleans per year ($300 value)",
      "Four salt cell cleans per year ($200 value)",
      "All specialty chemicals included (fair-use)",
      "Priority scheduling (route priority, faster response)",
      "On-route minor repairs: labor included up to 30 min/visit",
      "Parts billed at Leslie's retail pricing",
      "90-day labor warranty on repairs",
      "⚠️ Off-route dedicated service visits: $199",
      "⚠️ Requires month-to-month, 30-day cancellation notice"
    ],
    featuresFull: {
      weeklyService: [
        { name: "Equipment Visual Inspection", basic: true, full: true, premium: true, lux: true },
        { name: "Chemical Testing & Water Balancing", basic: true, full: true, premium: true, lux: true },
        { name: "Emptying Skimmer & Pump Baskets", basic: true, full: true, premium: true, lux: true },
        { name: "Skimming Surface Debris", basic: false, full: true, premium: true, lux: true },
        { name: "Brushing Walls, Steps, and Tile Line", basic: false, full: true, premium: true, lux: true },
        { name: "Manual Vacuuming as Needed", basic: false, full: true, premium: true, lux: true },
        { name: "Backwashing as Needed", basic: false, full: true, premium: true, lux: true }
      ],
      reporting: [
        { name: "Digital Service Log w/ Time Stamp", basic: true, full: true, premium: true, lux: true },
        { name: "Text/Email Photo Report After Visit", basic: true, full: true, premium: true, lux: true },
        { name: "Realtime Smart Water Monitoring Integration (coming soon)", basic: false, full: false, premium: false, lux: true }
      ],
      specialtyChemicals: [
        { name: "Phosphate Remover (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Enzyme Treatments (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Stabilizer (CYA) Monitoring & Dosing (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Algaecide Treatments (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Scale & Calcium Prevention (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Aromatherapy/Spa Chemical Upgrades", basic: false, full: false, premium: false, lux: true }
      ],
      periodicMaintenance: [
        { name: "Filter Deep Cleaning (2×/year)", value: "$300 value ($150 x 2 times/year)", basic: false, full: false, premium: true, lux: true },
        { name: "Salt Cell Cleaning (4×/year)", value: "$200 value ($50 x 4 times/year)", basic: false, full: false, premium: true, lux: true }
      ],
      repairsWarranty: [
        { name: "On-Route Minor Repairs (up to 30 min labor)", value: "Gaskets, o-rings, minor part replacements", basic: false, full: false, premium: true, lux: true },
        { name: "ALL Repair Labor Covered (no caps)", value: "Unlimited labor coverage, parts at retail", basic: false, full: false, premium: false, lux: true },
        { name: "90-Day Labor Warranty on Repairs", basic: false, full: false, premium: true, lux: true },
        { name: "ALL Equipment Fully Warrantied", value: "Pumps, heaters, automation - everything", basic: false, full: false, premium: false, lux: true }
      ],
      prioritySupport: [
        { name: "Priority Scheduling (Route Priority)", basic: false, full: false, premium: true, lux: true },
        { name: "Priority Repair Scheduling (Front of Queue)", basic: false, full: false, premium: true, lux: true },
        { name: "Same-Day Emergency Response", value: "$299 dispatch fee", basic: false, full: false, premium: true, lux: true },
        { name: "Dedicated Concierge Contact (Direct Cell Access)", basic: false, full: false, premium: false, lux: true }
      ]
    }
  },
  lux: {
    id: "lux",
    name: "Lux",
    icon: Crown,
    price: 580,
    displayPrice: "$580",
    regularPrice: "$1,500+",
    period: "month",
    tier: 3,
    popular: false,
    gradient: "from-gold/30 to-luxury/30",
    shortDescription: "Pool concierge - zero headaches, luxury lifestyle",
    longDescription: "The ultimate pool experience with zero headaches. Everything in Premium plus all repair labor included, fresh towels, sunscreen station, lifeguard services, dedicated concierge support, and priority same-day response.",
    featuresAbbreviated: [
      "Everything in Premium",
      "ALL repair labor included",
      "Fresh pool towels weekly",
      "Dedicated concierge contact"
    ],
    featuresStandard: [
      "Everything in Premium package +",
      "ALL repair labor included (parts at Leslie's retail)",
      "Dedicated concierge contact (direct cell access)",
      "Priority same-day response",
      "Weekly deck/leaf blowing (light maintenance)",
      "Fresh pool towels (delivered & washed weekly)",
      "Sunscreen station (premium SPF 50 stocked)",
      "Pool party prep & cleanup (2×/year)",
      "On-demand lifeguard (2 events/year, subject to availability)",
      "Smart monitoring integration",
      "⚠️ Requires 12-month minimum contract",
      "⚠️ Early termination fee: 2 months"
    ],
    featuresFull: {
      weeklyService: [
        { name: "Equipment Visual Inspection", basic: true, full: true, premium: true, lux: true },
        { name: "Chemical Testing & Water Balancing", basic: true, full: true, premium: true, lux: true },
        { name: "Emptying Skimmer & Pump Baskets", basic: true, full: true, premium: true, lux: true },
        { name: "Skimming Surface Debris", basic: false, full: true, premium: true, lux: true },
        { name: "Brushing Walls, Steps, and Tile Line", basic: false, full: true, premium: true, lux: true },
        { name: "Manual Vacuuming as Needed", basic: false, full: true, premium: true, lux: true },
        { name: "Backwashing as Needed", basic: false, full: true, premium: true, lux: true }
      ],
      reporting: [
        { name: "Digital Service Log w/ Time Stamp", basic: true, full: true, premium: true, lux: true },
        { name: "Text/Email Photo Report After Visit", basic: true, full: true, premium: true, lux: true },
        { name: "Realtime Smart Water Monitoring Integration", basic: false, full: false, premium: false, lux: true }
      ],
      specialtyChemicals: [
        { name: "Phosphate Remover (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Enzyme Treatments (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Stabilizer (CYA) Monitoring & Dosing (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Algaecide Treatments (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Scale & Calcium Prevention (as needed)", basic: false, full: false, premium: true, lux: true },
        { name: "Aromatherapy/Spa Chemical Upgrades", basic: false, full: false, premium: false, lux: true }
      ],
      periodicMaintenance: [
        { name: "Filter Deep Cleaning (2×/year)", value: "$300 value ($150 x 2 times/year)", basic: false, full: false, premium: true, lux: true },
        { name: "Salt Cell Cleaning (4×/year)", value: "$200 value ($50 x 4 times/year)", basic: false, full: false, premium: true, lux: true }
      ],
      repairsWarranty: [
        { name: "On-Route Minor Repairs (up to 30 min labor)", value: "Gaskets, o-rings, minor part replacements", basic: false, full: false, premium: true, lux: true },
        { name: "ALL Repair Labor Covered (no caps)", value: "Unlimited labor coverage, parts at retail", basic: false, full: false, premium: false, lux: true },
        { name: "90-Day Labor Warranty on Repairs", basic: false, full: false, premium: true, lux: true },
        { name: "ALL Equipment Fully Warrantied", value: "Pumps, heaters, automation - everything", basic: false, full: false, premium: false, lux: true }
      ],
      prioritySupport: [
        { name: "Priority Scheduling (Route Priority)", basic: false, full: false, premium: true, lux: true },
        { name: "Priority Repair Scheduling (Front of Queue)", basic: false, full: false, premium: true, lux: true },
        { name: "Same-Day Emergency Response", value: "$299 dispatch fee (priority for Premium/Lux)", basic: false, full: false, premium: true, lux: true },
        { name: "Dedicated Concierge Contact (Direct Cell Access)", basic: false, full: false, premium: false, lux: true }
      ],
      luxuryAmenities: [
        { name: "Fresh Pool Towels (Delivered & Washed Weekly)", value: "$50/month value", basic: false, full: false, premium: false, lux: true },
        { name: "Sunscreen Station (Premium SPF 50 Always Stocked)", value: "$30/month value", basic: false, full: false, premium: false, lux: true },
        { name: "Pool Party Preparation (2×/year)", value: "Super shock, setup, final check", basic: false, full: false, premium: false, lux: true },
        { name: "Pool Party Cleanup (2×/year)", value: "Deep clean, rebalancing", basic: false, full: false, premium: false, lux: true },
        { name: "Lifeguard Services On-Demand (2 events/year)", value: "Subject to availability and local compliance", basic: false, full: false, premium: false, lux: true },
        { name: "Leaf Blowing & Deck Maintenance (Weekly)", value: "Light maintenance, not landscaping", basic: false, full: false, premium: false, lux: true },
        { name: "Smart Pool Monitoring Integration", value: "Real-time alerts, remote control", basic: false, full: false, premium: false, lux: true }
      ]
    }
  }
};

export const serviceTiersArray = Object.values(serviceTiersData);

// Helper function to generate select options for forms
export const getServiceTierSelectOptions = () => {
  return serviceTiersArray.map(tier => ({
    value: tier.id,
    label: `${tier.name} - ${tier.displayPrice}/${tier.period}${tier.id === 'premium' ? ' (White-Glove ⭐ Most Popular)' : tier.id === 'lux' ? ' (Pool Concierge - 12-mo contract)' : ''}`,
    description: tier.shortDescription
  }));
};

// Helper function to format pricing display
export const formatTierPricing = (tier: ServiceTier): string => {
  return `${tier.displayPrice}/${tier.period}`;
};

// Helper function to get price range across all tiers
export const getMonthlyServicePriceRange = (): string => {
  const prices = serviceTiersArray.map(tier => tier.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return `$${min}-${max}`;
};

// Helper function to get tier by name or id
export const getTierById = (id: string): ServiceTier | undefined => {
  return serviceTiersData[id];
};

export const getTierByName = (name: string): ServiceTier | undefined => {
  return serviceTiersArray.find(tier => 
    tier.name.toLowerCase() === name.toLowerCase()
  );
};

// One-time service pricing (not monthly tiers)
export const inspectionServices = {
  basic: {
    name: "Basic Inspection",
    price: "$150",
    description: "Visual equipment check and basic safety assessment"
  },
  comprehensive: {
    name: "Comprehensive Inspection",
    price: "$250",
    description: "Complete equipment testing and full safety evaluation",
    popular: true
  },
  prePurchase: {
    name: "Pre-Purchase Inspection",
    price: "$350",
    description: "Everything in Comprehensive plus code compliance and future cost analysis"
  }
} as const;

export const seasonalServices = {
  springStartup: {
    name: "Spring Startup",
    priceRange: "$200-350",
    description: "One-time service based on pool condition"
  },
  vacationCare: {
    name: "Vacation Care",
    priceRange: "$75-150",
    description: "Per visit temporary service"
  }
} as const;
